import { useContext, useCallback } from "react";
import { AuthContext } from "src/utils/AuthContext.js";
import { useStripe } from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";
import { useIndexedDB } from "src/utils/useIndexedDB.js";
import { getApiRoute, getPageRoute } from "src/services/Config.js";
import axios from "axios";
import { customEvent } from "./gtag.js";
import {
  Features,
  getUpgradeFlow,
  hasAccess,
  hasValidConsumerSubscription,
} from "src/services/FeatureGatingService.js";

export const paymentStateDefaultValue = {
  simplePaymentFailed: false,
};

export const useSimplePayment = ({
  type = "consumer_annual",
  trialPeriodDays = 7,
  onSuccess = () => {},
}) => {
  const { refetchAuth, user } = useContext(AuthContext);
  const stripe = useStripe();
  const navigate = useNavigate();
  const [
    paymentStates,
    setPaymentStates,
    { setSimplePaymentFailed },
    dbLoading,
  ] = useIndexedDB(
    "stanza",
    "states",
    `payment-${user?._id ?? "guest"}`,
    paymentStateDefaultValue
  );
  const { simplePaymentFailed } = paymentStates;
  const isSimplePayment = !!user?.stripeCustomerId && !simplePaymentFailed;

  const shouldTrySimplePayment = useCallback(
    (featureKey) => {
      return (
        isSimplePayment &&
        !hasAccess(user, featureKey) &&
        !hasValidConsumerSubscription(user)
      );
    },
    [isSimplePayment, user]
  );

  const handleSimplePayment = useCallback(
    async ({ featureKey }) => {
      try {
        const source = Features[featureKey].upgradeSource;
        customEvent({
          name: `handled_simple_payment_${source}`,
          category: "Upgrade",
          label: "Successful Simple Payment",
          data: {
            user_id: user._id,
            user_email: user.email,
            user_handle: user.handle ?? "no_handle",
            trial_period: trialPeriodDays > 0,
          },
        });
        const response = await axios.post(
          getApiRoute("upgrade", "STRIPE_NATIVE_CHECKOUT"),
          {
            type,
            trialPeriodDays,
          },
          { withCredentials: true }
        );

        const { clientSecret } = response.data;

        if (clientSecret) {
          const { error: confirmError } = await stripe.confirmCardPayment(
            clientSecret
          );
          if (confirmError) {
            throw confirmError;
          }
        } else if (!trialPeriodDays) {
          throw new Error("Failed on starting checkout session.");
        }
        customEvent({
          name: `simple_payment_successful`,
          category: "Upgrade",
          label: "Successful Simple Payment",
          data: {
            user_id: user._id,
            user_email: user.email,
            user_handle: user.handle ?? "no_handle",
            trial_period: trialPeriodDays > 0,
          },
        });
        await new Promise((resolve) => setTimeout(resolve, 2000));
        await refetchAuth();
        onSuccess();
        return true;
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "An error occurred";
        // await for async db save before going to upgrade consumer page
        await setSimplePaymentFailed(true);
        customEvent({
          name: `simple_payment_failure`,
          category: "Upgrade",
          label: "Failure Simple Payment",
          data: {
            user_id: user._id,
            user_email: user.email,
            user_handle: user.handle ?? "no_handle",
            trial_period: trialPeriodDays > 0,
            message: errorMessage,
          },
        });
        const upgradeFlow = getUpgradeFlow(featureKey);
        navigate(upgradeFlow.route, { state: upgradeFlow.state });
        return false;
      }
    },
    [
      navigate,
      onSuccess,
      refetchAuth,
      setSimplePaymentFailed,
      stripe,
      trialPeriodDays,
      type,
      user,
    ]
  );

  return {
    handleSimplePayment,
    paymentStates,
    setPaymentStates,
    setSimplePaymentFailed,
    isSimplePayment,
    shouldTrySimplePayment,
    dbLoading,
  };
};
